import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ProductGrid } from '../components/ProductGrid';
import { getProducts, filterProducts, sortProducts } from '../data/products';
import { Product, ProductFilters } from '../types';

export const Collection = () => {
  const { collectionName } = useParams<{ collectionName: string }>();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [sortBy, setSortBy] = useState('popular');
  const [isLoading, setIsLoading] = useState(true);

  // Load products and apply collection filter
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const productsData = await getProducts();
        setProducts(productsData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Apply collection filter and sorting
  useEffect(() => {
    if (products.length > 0 && collectionName) {
      let filters: ProductFilters = {};

      // Apply collection-specific filters
      switch (collectionName) {
        case 'new':
          filters.isNew = true;
          break;
        case 'sale':
          filters.isSale = true;
          break;
        case 'premium':
        case 'featured':
          filters.isNew = true;
          filters.isSale = true;
          break;
        default:
          // For other collections, try to match category
          const categories = products.map(p => p.category.toLowerCase());
          if (categories.includes(collectionName.toLowerCase())) {
            filters.category = collectionName;
          }
      }

      // Apply additional filters from URL params
      const category = searchParams.get('category');
      if (category) filters.category = category;

      const search = searchParams.get('search');
      if (search) filters.search = search;

      let filtered = filterProducts(products, filters);
      filtered = sortProducts(filtered, sortBy);
      setFilteredProducts(filtered);
    }
  }, [products, collectionName, searchParams, sortBy]);

  const getCollectionTitle = () => {
    switch (collectionName) {
      case 'new':
        return 'New Arrivals';
      case 'sale':
        return 'Sale Items';
      case 'premium':
      case 'featured':
        return 'Featured Products';
      default:
        return collectionName ? collectionName.charAt(0).toUpperCase() + collectionName.slice(1) : 'Collection';
    }
  };

  const getCollectionDescription = () => {
    switch (collectionName) {
      case 'new':
        return 'Discover our latest collection of trendy and fashionable products.';
      case 'sale':
        return 'Don\'t miss out on these amazing deals. Limited time offers on selected items.';
      case 'premium':
      case 'featured':
        return 'Discover our handpicked collection of premium products that combine style, quality, and comfort.';
      default:
        return `Explore our ${collectionName} collection.`;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading collection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="container section-padding">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-charcoal-900 mb-4">
            {getCollectionTitle()}
          </h1>
          <p className="text-lg text-charcoal-600 max-w-2xl mx-auto">
            {getCollectionDescription()}
          </p>
        </div>

        <div className="flex justify-between items-center mb-6">
          <p className="text-charcoal-600">
            {filteredProducts.length} products found
          </p>
          <div className="flex items-center space-x-4">
            <label htmlFor="sort" className="text-charcoal-700 text-sm">Sort by:</label>
            <select
              id="sort"
              className="input w-auto"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="popular">Popularity</option>
              <option value="newest">Newest Arrivals</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A-Z</option>
              <option value="name-desc">Name: Z-A</option>
            </select>
          </div>
        </div>

        {filteredProducts.length > 0 ? (
          <ProductGrid products={filteredProducts} columns={4} />
        ) : (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500">
              Try browsing our other collections or use different filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
